// components/GeoSearch.tsx
import React, { useState, useRef, useEffect } from 'react';
import { Search, MapPin, X, Loader2, Globe, Building2, Landmark } from 'lucide-react';
import { useGeoSearch } from './hooks/useGeoSearch';
import type { GeoCollection, SearchResult } from './type';

interface GeoSearchProps {
  geoData: GeoCollection | null;
  /** Callback quand l'utilisateur sélectionne une ville */
  onSelect: (result: SearchResult) => void;
  placeholder?: string;
  /** Filtre par type : ['ADM3'] = communes uniquement */
  types?: string[];
  className?: string;
}

const TYPE_LABELS: Record<string, string> = {
  ADM1: 'Région',
  ADM2: 'Département',
  ADM3: 'Commune',
  ADM4: 'Arrondissement',
};

const TYPE_ICONS: Record<string, React.ReactNode> = {
  ADM1: <Globe className="w-4 h-4 text-purple-500" />,
  ADM2: <Landmark className="w-4 h-4 text-indigo-500" />,
  ADM3: <Building2 className="w-4 h-4 text-emerald-500" />,
};

export const GeoSearch: React.FC<GeoSearchProps> = ({
  geoData,
  onSelect,
  placeholder = 'Rechercher une ville, commune, département…',
  types,
  className = '',
}) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { results, totalCount, isSearching } = useGeoSearch(geoData, query, {
    types,
    limit: 15,
  });

//   console.log(results)

  // Fermer au clic extérieur
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset l'index quand les résultats changent
  useEffect(() => {
    setHighlightedIndex(0);
  }, [results]);

  // Navigation clavier
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) setIsOpen(true);

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((i) => Math.min(i + 1, results.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((i) => Math.max(i - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (results[highlightedIndex]) {
          handleSelect(results[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        inputRef.current?.blur();
        break;
    }
  };

  const handleSelect = (result: SearchResult) => {
    setQuery(result.name);
    setIsOpen(false);
    onSelect(result);
  };

  const handleClear = () => {
    setQuery('');
    setHighlightedIndex(0);
    inputRef.current?.focus();
  };

  return (
    <div ref={containerRef} className={`relative w-full ${className}`}>
      {/* Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="w-full pl-10 pr-10 py-3 bg-white border border-gray-200 rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all shadow-sm"
        />

        {/* Loader / Clear */}
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {isSearching && <Loader2 className="w-4 h-4 text-indigo-500 animate-spin" />}
          {query && !isSearching && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Effacer"
            >
              <X className="w-3.5 h-3.5 text-gray-500" />
            </button>
          )}
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-40 top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden max-h-[400px] overflow-y-auto">
          {/* Header info */}
          <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 text-xs text-gray-500 flex items-center justify-between">
            <span>
              {results.length > 0
                ? `${results.length} résultat${results.length > 1 ? 's' : ''}`
                : 'Aucun résultat'}
            </span>
            {!query && <span>{totalCount} localités disponibles</span>}
          </div>

          {/* Liste */}
          {results.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-gray-500">
              <MapPin className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              Aucune localité trouvée pour « {query} »
            </div>
          ) : (
            <ul className="py-1">
              {results.map((r, idx) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => handleSelect(r)}
                    onMouseEnter={() => setHighlightedIndex(idx)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                      idx === highlightedIndex ? 'bg-indigo-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                      {TYPE_ICONS[r.type] ?? <MapPin className="w-4 h-4 text-gray-500" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {r.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {TYPE_LABELS[r.type] ?? r.type} · {r.group}
                      </p>
                    </div>
                    <span className="text-[10px] font-semibold text-gray-400 uppercase">
                      {r.type}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};